import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import cn from 'classnames';
import { isBefore, isSameDay } from 'date-fns';
import { Callout, ConfirmationModal, KeyboardDatePicker, useDevice, useOverlayContext } from 'seagull';
import TimeSelector from '~/components/TimeSelector/TimeSelector';
import { getCurrentTimeSlot } from '~/components/TimeSelector/TimeSelector.utils';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { useIoContext } from '~/providers/IoContext';
import styles from './ScheduleProposal.module.scss';
const MAX_DATE_MONTHS = 3;
export default function ScheduleProposal(param) {
    let { dismissModal, draftId } = param;
    const { t: texts } = useLocaleKeys('components.agreementStudio.scheduleProposal');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { showSuccessToast, showErrorToast } = useOverlayContext();
    const { getNow } = useIoContext();
    const { api, logBI } = useStoreActions();
    const { isMobileDevice } = useDevice();
    const maxDate = new Date(getNow());
    maxDate.setMonth(maxDate.getMonth() + MAX_DATE_MONTHS);
    const [date, setDate] = useState(getNow());
    const [dateTimeValue, setDateTimeValue] = useState(getCurrentTimeSlot(getNow(), getNow()).dateTimeValue);
    const handleTimeChange = useCallback((time)=>{
        setDateTimeValue(time);
    }, []);
    const handleDateChange = useCallback((date)=>{
        if (date) {
            const selectedDate = new Date(date.toISOString());
            setDate(isSameDay(selectedDate, getNow()) ? new Date(getNow()) : selectedDate);
        }
    }, [
        getNow
    ]);
    const handleSubmit = async ()=>{
        logBI(10494);
        if (!draftId) {
            showErrorToast(texts.draftError(), 'schedule-proposal-draft-error');
            return;
        }
        if (isBefore(new Date(dateTimeValue), getNow())) {
            showErrorToast(texts.timePassedError(), 'schedule-proposal-error');
            return;
        }
        const { success } = await handleRequest(api.draft.createScheduledProposal({
            draftId,
            time: dateTimeValue
        }));
        if (success) {
            showSuccessToast(texts.proposalScheduledSuccess(), 'schedule-proposal-success');
            dismissModal(true);
        } else {
            showErrorToast(texts.somethingWentWrong(), 'schedule-proposal-error');
        }
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'schedule_proposal_confirm'
            },
            secondaryButton: {
                name: 'schedule_proposal_cancel'
            }
        },
        title: texts.title(),
        textAlignCenter: false,
        width: "638px",
        onPrimaryButtonClick: handleSubmit,
        onSecondaryButtonClick: ()=>dismissModal(false),
        primaryButtonLabel: texts.scheduleSend(),
        primaryButtonTestId: "schedule-proposal-send",
        secondaryButtonLabel: commonButtons.back(),
        secondaryButtonTestId: "schedule-proposal-cancel",
        content: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    "data-testid": "schedule-proposal-modal",
                    className: cn(styles.modalContent, {
                        [styles.mobile]: isMobileDevice
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(KeyboardDatePicker, {
                            bi: {
                                name: 'schedule_proposal_date'
                            },
                            testId: "scheduled-proposal-date",
                            className: styles.datePicker,
                            minDate: getNow(),
                            maxDate: maxDate,
                            value: date,
                            onChange: handleDateChange
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(TimeSelector, {
                                onTimeChange: handleTimeChange,
                                testId: "schedule-proposal-time-selector",
                                currentDate: date
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Callout, {
                    testId: "schedule-proposal-callout",
                    children: texts.calloutText()
                })
            ]
        })
    });
}
